// Migration utility to initialize API mode
// This replaces localStorage-based data with API-based data management
import config from '../config';

/**
 * Initialize the application to use API mode instead of localStorage
 * @returns {Promise<boolean>} Success status of the initialization
 */
export const initializeApiMode = async () => {
  try {
    // Detect standalone client mode
    const isStandaloneClient = window.electronAPI && !window.location.href.includes('localhost');
    
    if (isStandaloneClient) {
      console.log('🎯 STANDALONE CLIENT MODE - Using offline mode for client distribution');
      console.log('📱 No backend server needed - working with local data only');
      setApiAvailability(false);
      return false;
    }
    
    // Development mode - try to connect to API
    const apiUrl = config.apiUrl;
    console.log('Checking API health at:', apiUrl);
    
    // Set a timeout for the fetch to avoid long waits
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // Increased timeout
    
    try {
      // Try multiple times with increasing backoff
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const response = await fetch(`${apiUrl}/health`, { 
            method: 'GET',
            signal: controller.signal
          });
          
          if (response.ok) {
            console.log('✅ API connection successful on attempt', attempt);
            clearTimeout(timeoutId);
            setApiAvailability(true);
            return true;
          }
          console.warn(`API health check failed on attempt ${attempt}`);
          
          // Wait before next attempt (exponential backoff)
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, attempt * 1000));
          }
        } catch (attemptError) {
          console.warn(`API connection failed on attempt ${attempt}:`, attemptError.message);
          
          // Wait before next attempt (exponential backoff)
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, attempt * 1000));
          }
        }
      }
    } catch (fetchError) {
      console.warn('API health check timed out or failed:', fetchError.message);
    } finally {
      clearTimeout(timeoutId);
    }
    
    // Return false to indicate API is not available
    console.log('⚠️ API unavailable - application will have limited functionality');
    setApiAvailability(false);
    return false;
  } catch (error) {
    console.error('❌ Critical error initializing API mode:', error);
    // Return false to indicate API initialization failed
    setApiAvailability(false);
    return false;
  }
};

// Track API availability status
let apiAvailable = null;

/**
 * Check if the API is currently available
 * @returns {boolean|null} True if API is available, false if not, null if unknown
 */
export const isApiAvailable = () => apiAvailable;

/**
 * Set the API availability status (used internally by initializeApiMode)
 * @param {boolean} available - Whether the API is available
 */
export const setApiAvailability = (available) => {
  apiAvailable = available;
};

/**
 * Check if the application is currently using API mode
 * @returns {boolean} True if using API mode, false if using localStorage
 */
export const isApiMode = () => true; // Always API mode, but availability may vary

/**
 * Get the API base URL
 * @returns {string} The API base URL
 */
export const getApiUrl = () => config.apiUrl;
